<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { foundingYearRange } from '$lib/stores/selection';

  /**
   * Embedded in a guided step, which supplies its own heading and padding.
   *
   * The checkbox stays even so: it is the "filter by founding year at all"
   * question, and turning it on writes [1800, this year] — a range that reads
   * as "any" but excludes every company whose founding year is unknown. An
   * optional step has to be able to be left alone.
   */
  export let embedded = false;

  let useYearFilter = false;

  const minYear = 1800;
  const maxYear = new Date().getFullYear(); // 2025

  let yearFrom = minYear;
  let yearTo = maxYear;
  let sliderElement: HTMLElement | undefined;
  let isDraggingMin = false;
  let isDraggingMax = false;

  // Handle checkbox toggle
  function toggleYearFilter() {
    useYearFilter = !useYearFilter;
    if (!useYearFilter) {
      foundingYearRange.set([null, null]);
      yearFrom = minYear;
      yearTo = maxYear;
    } else {
      foundingYearRange.set([yearFrom, yearTo]);
    }
  }

  // Sync checkbox when filter is changed externally (from filter pills or AI)
  $: if ($foundingYearRange[0] === null && $foundingYearRange[1] === null) {
    useYearFilter = false;
    yearFrom = minYear;
    yearTo = maxYear;
  } else if ($foundingYearRange[0] !== null || $foundingYearRange[1] !== null) {
    useYearFilter = true;
    if ($foundingYearRange[0] !== null) yearFrom = $foundingYearRange[0];
    if ($foundingYearRange[1] !== null) yearTo = $foundingYearRange[1];
  }

  // Keep years valid
  $: if (yearFrom > yearTo) {
    if (yearFrom >= maxYear) {
      yearFrom = maxYear;
    }
    yearTo = yearFrom;
  }

  // Handle slider drag events
  function handleMouseMove(e: MouseEvent) {
    if (!isDraggingMin && !isDraggingMax) return;

    if (!sliderElement) return;

    const rect = sliderElement.getBoundingClientRect();
    const percent = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const value = Math.round((percent / 100) * (maxYear - minYear)) + minYear;

    if (isDraggingMin && value <= yearTo) {
      yearFrom = value;
    } else if (isDraggingMax && value >= yearFrom) {
      yearTo = value;
    }
  }

  function handleMouseUp() {
    isDraggingMin = false;
    isDraggingMax = false;
  }

  // Update store when slider values change (only if filter is active)
  $: if (useYearFilter) {
    foundingYearRange.set([yearFrom, yearTo]);
  }
</script>

<svelte:window on:mousemove={handleMouseMove} on:mouseup={handleMouseUp} />

<div class={embedded ? '' : useYearFilter ? 'm-2 rounded-control bg-surface-well p-3' : 'p-3'}>
  <!--
    The checkbox belongs to the sidebar only.

    Embedded, the guided step asks "filter by founding year?" before showing
    this at all, so the panel's own tick would be the same question twice. The
    step drives the store instead: asking for the filter writes the widest
    range, which is what this checkbox did.
  -->
  {#if !embedded}
    <div class="mb-3">
      <label class="group flex cursor-pointer items-center">
        <input
          type="checkbox"
          checked={useYearFilter}
          on:click={toggleYearFilter}
          class="mr-2 h-3 w-3 rounded border-gray-300 text-accent-600 focus:ring-1 focus:ring-accent-200"
        />
        <svg class="filter-mark mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span
          class="text-xs font-semibold text-gray-900 transition-colors group-hover:text-gray-700"
        >
          {$_('filters.founding.year')}
        </span>
      </label>
    </div>
  {/if}

  {#if useYearFilter}
    <div class="space-y-4">
      <!-- Custom Slider -->
      <div class="relative mb-6">
        <!-- Slider track -->
        <div class="relative h-1 rounded-full bg-gray-200" bind:this={sliderElement}>
          <!-- Active range -->
          <div
            class="absolute h-full rounded-full bg-accent-500"
            style="left: {((yearFrom - minYear) / (maxYear - minYear)) * 100}%; width: {((yearTo -
              yearFrom) /
              (maxYear - minYear)) *
              100}%"
          ></div>

          <!-- Min handle -->
          <div
            class="absolute top-1/2 -translate-y-1/2 cursor-grab"
            style="left: {((yearFrom - minYear) / (maxYear - minYear)) * 100}%"
            on:mousedown={(e) => {
              e.preventDefault();
              isDraggingMin = true;
            }}
            role="slider"
            tabindex="0"
            aria-label={$_('a11y.minimumYear')}
            aria-valuemin={minYear}
            aria-valuemax={yearTo}
            aria-valuenow={yearFrom}
          >
            <div
              class="h-4 w-4 -translate-x-1/2 rounded-full bg-white shadow-raised ring-2 ring-accent-500 transition-colors"
            ></div>
            <div
              class="pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 rounded bg-gray-800 px-2 py-0.5 text-xs text-white opacity-0 transition-opacity {isDraggingMin
                ? 'opacity-100'
                : ''}"
            >
              {yearFrom}
            </div>
          </div>

          <!-- Max handle -->
          <div
            class="absolute top-1/2 -translate-y-1/2 cursor-grab"
            style="left: {((yearTo - minYear) / (maxYear - minYear)) * 100}%"
            on:mousedown={(e) => {
              e.preventDefault();
              isDraggingMax = true;
            }}
            role="slider"
            tabindex="0"
            aria-label={$_('a11y.maximumYear')}
            aria-valuemin={yearFrom}
            aria-valuemax={maxYear}
            aria-valuenow={yearTo}
          >
            <div
              class="h-4 w-4 -translate-x-1/2 rounded-full bg-white shadow-raised ring-2 ring-accent-500 transition-colors"
            ></div>
            <div
              class="pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 rounded bg-gray-800 px-2 py-0.5 text-xs text-white opacity-0 transition-opacity {isDraggingMax
                ? 'opacity-100'
                : ''}"
            >
              {yearTo}
            </div>
          </div>
        </div>

        <!-- Reference points -->
        <div class="mt-2 flex justify-between text-2xs text-gray-500">
          <span>{minYear}</span>
          <span>1900</span>
          <span>1950</span>
          <span>2000</span>
          <span>{maxYear}</span>
        </div>
      </div>

      <!-- Numeric Inputs -->
      <div class="flex gap-2">
        <div class="flex-1">
          <label class="mb-1 block text-xs text-gray-500">{$_('filters.capital.from')}</label>
          <input
            type="number"
            bind:value={yearFrom}
            min={minYear}
            max={yearTo}
            step={1}
            class="w-full rounded-control border border-gray-300 px-2 py-1 text-center text-xs focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
          />
        </div>
        <div class="flex-1">
          <label class="mb-1 block text-xs text-gray-500">{$_('filters.capital.to')}</label>
          <input
            type="number"
            bind:value={yearTo}
            min={yearFrom}
            max={maxYear}
            step={1}
            class="w-full rounded-control border border-gray-300 px-2 py-1 text-center text-xs focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
          />
        </div>
      </div>
    </div>
  {/if}
</div>
